import { useEffect, useMemo, useState } from 'react';
import DetectRTC from 'detectrtc';

export type MicPermission = 'granted' | 'denied' | 'prompt' | 'unsupported' | 'unknown';

export interface MicrophonePermissionState {
  permission: MicPermission;
  hasMicrophone: boolean | undefined;
  loading: boolean;
  refresh: () => void;
}

/**
 * Cross-browser microphone permission checker.
 * 
 * - Uses Permissions API when available (Chrome/Edge/Firefox)
 * - Falls back to DetectRTC for Safari/older browsers
 * - Listens to device changes to re-evaluate state
 */
export function useMicrophonePermission(): MicrophonePermissionState {
  const [permission, setPermission] = useState<MicPermission>('unknown');
  const [hasMicrophone, setHasMicrophone] = useState<boolean | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const hasPermissionsApi = useMemo(() => typeof navigator !== 'undefined' && !!(navigator as any).permissions?.query, []);

  const evaluateWithDetectRTC = () => {
    setLoading(true);
    try {
      DetectRTC.load(() => {
        setHasMicrophone(!!DetectRTC.hasMicrophone);
        const granted = !!DetectRTC.isWebsiteHasMicrophonePermissions;
        setPermission(granted ? 'granted' : 'denied');
        setLoading(false);
      });
    } catch {
      setPermission('unsupported');
      setLoading(false);
    }
  };

  const evaluateWithPermissionsApi = async () => {
    setLoading(true);
    try {
      // Check for physical mic presence as well
      if (navigator?.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasMic = devices.some((d) => d.kind === 'audioinput');
        setHasMicrophone(hasMic);
      } else {
        setHasMicrophone(undefined);
      }

      const status: PermissionStatus = await (navigator as any).permissions.query({ name: 'microphone' as any });
      setPermission((status.state as MicPermission) ?? 'unknown');

      // React to permission changes
      const handleChange = () => setPermission((status.state as MicPermission) ?? 'unknown');
      status.onchange = handleChange;
      setLoading(false);
    } catch {
      // Fall back if browser throws on querying microphone
      evaluateWithDetectRTC();
    }
  };

  const refresh = () => {
    if (hasPermissionsApi) {
      void evaluateWithPermissionsApi();
    } else {
      evaluateWithDetectRTC();
    }
  };

  useEffect(() => {
    refresh();

    const onDeviceChange = () => refresh();
    try {
      navigator?.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    } catch {
      // no-op
    }
    return () => {
      try {
        navigator?.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
      } catch {
        // no-op
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermissionsApi]);

  return { permission, hasMicrophone, loading, refresh };
}

